import {
  LoginRequiredError,
  type OnRefreshResponse,
  type User,
  getClaims,
  useAuth,
} from '@workos-inc/authkit-react';
import { useEffect, useMemo, useSyncExternalStore } from 'react';

import { AUTH_CONFIGURED } from './config';
import { type AuthSession, type AuthUser, loginRequiredError } from './types';

type AuthKitContext = ReturnType<typeof useAuth>;

/** The part of AuthKit's client the session methods call. */
export type AuthKitClient = Pick<AuthKitContext, 'getAccessToken' | 'getUser' | 'signIn' | 'signOut'>;

type SessionMethods = Omit<AuthSession, 'isAuthenticated' | 'isLoading' | 'user' | 'error'>;

/**
 * Access-token claims that say who the person is in Studio's terms. WorkOS
 * puts its own ids in `sub` and `org_id`; Studio's JWT template adds the ids
 * Studio has always keyed things by (`studio_uid`, `studio_org`, the WorkOS
 * `external_id`s) and the organization's name.
 */
const IDENTITY_CLAIMS = ['studio_uid', 'studio_org', 'org_id', 'org_name'] as const;

type IdentityClaims = Partial<Record<(typeof IDENTITY_CLAIMS)[number], string>>;

interface SessionSnapshot {
  identity: IdentityClaims;
  /** Set by `logout({ openUrl: false })`; AuthKit itself keeps reporting the user. */
  signedOut: boolean;
}

let snapshot: SessionSnapshot = { identity: {}, signedOut: false };
const listeners = new Set<() => void>();

function setSnapshot(next: SessionSnapshot): void {
  snapshot = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function readSnapshot(): SessionSnapshot {
  return snapshot;
}

function identityClaims(claims: Record<string, unknown>): IdentityClaims {
  const identity: IdentityClaims = {};
  for (const key of IDENTITY_CLAIMS) {
    const value = claims[key];
    if (typeof value === 'string' && value.trim()) identity[key] = value.trim();
  }
  return identity;
}

function sameIdentity(a: IdentityClaims, b: IdentityClaims): boolean {
  return IDENTITY_CLAIMS.every((key) => a[key] === b[key]);
}

/**
 * `AuthKitProvider`'s `onRefresh`: runs on sign-in and on every token refresh.
 * AuthKit's `user` is the WorkOS profile only, so the organization the
 * session is in has to come from the token. The snapshot is only replaced
 * when those claims change, so a routine refresh does not hand consumers a
 * new `user` object.
 */
export function rememberWorkOSSession({ accessToken }: Pick<OnRefreshResponse, 'accessToken'>): void {
  const identity = identityClaims(getClaims<Record<string, unknown>>(accessToken));
  if (snapshot.signedOut || !sameIdentity(snapshot.identity, identity)) {
    setSnapshot({ identity, signedOut: false });
  }
}

function markSignedOut(): void {
  setSnapshot({ identity: {}, signedOut: true });
}

/**
 * Studio's id for the person, the way the API resolves it: the JWT template's
 * `studio_uid`, else the WorkOS user's `external_id` (their Auth0-era id),
 * else the WorkOS user id. Existing code compares `user.sub` with the user
 * ids the API returns, so it has to be the Studio id, not the WorkOS one.
 */
function studioUserId(user: User, identity: IdentityClaims): string {
  return identity.studio_uid ?? (user.externalId || user.id);
}

function fullName(user: User): string | undefined {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return name || undefined;
}

/** The ID-token-shaped profile the rest of the app reads, plus the session's organization claims. */
export function workosAuthUser(user: User, identity: IdentityClaims = {}): AuthUser {
  return {
    ...identity,
    sub: studioUserId(user, identity),
    email: user.email,
    name: fullName(user),
    given_name: user.firstName ?? undefined,
    family_name: user.lastName ?? undefined,
    picture: user.profilePictureUrl ?? undefined,
  };
}

/** AuthKit's terminal "no session" becomes the error `hooks/useAuth` signs out on. */
function sessionError(error: unknown): unknown {
  return error instanceof LoginRequiredError ? loginRequiredError(error.message || undefined) : error;
}

const STUDIO_API_BASE_URL = import.meta.env.VITE_STUDIO_API_BASE_URL || 'http://localhost:8087';

/** Sign-in waits on this lookup, so a slow api means an unpinned sign-in rather than a stuck button. */
export const SIGN_IN_CONFIG_TIMEOUT_MS = 5000;

/**
 * The WorkOS organization Studio pins sign-in to, from the api's public
 * sign-in config. The api resolves it from Studio's organization id at
 * request time, so it follows that organization if it moves to another WorkOS
 * organization. Any failure means an unpinned sign-in rather than none.
 */
export async function studioSignInOrganizationId(fetcher: typeof fetch = fetch): Promise<string | undefined> {
  try {
    const response = await fetcher(`${STUDIO_API_BASE_URL}/api/studio/auth/config`, {
      cache: 'no-store',
      signal: AbortSignal.timeout(SIGN_IN_CONFIG_TIMEOUT_MS),
    });
    if (!response.ok) return undefined;
    const { organizationId } = (await response.json()) as { organizationId?: unknown };
    return typeof organizationId === 'string' && organizationId ? organizationId : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Session methods over an AuthKit client. `client` resolves once the
 * provider has finished starting up, so a call made while it is still
 * loading waits for the real client instead of reaching AuthKit's
 * placeholder, which answers every token request with "login required".
 */
export function createWorkOSSessionMethods(
  client: () => Promise<AuthKitClient>,
  onSignedOut: () => void,
  signInOrganizationId: () => Promise<string | undefined> = studioSignInOrganizationId,
): SessionMethods {
  return {
    async getAccessTokenSilently(options) {
      const authkit = await client();
      try {
        return await authkit.getAccessToken({ forceRefresh: options?.cacheMode === 'off' });
      } catch (error) {
        throw sessionError(error);
      }
    },

    /**
     * WorkOS issues no ID token. The access token's claims plus the profile
     * stand in for one, which is what the organization name lookup reads.
     */
    async getIdTokenClaims() {
      const authkit = await client();
      const user = authkit.getUser();
      if (!user) return undefined;
      const claims: Record<string, unknown> = await authkit.getAccessToken().then(
        (token) => getClaims<Record<string, unknown>>(token),
        () => ({}),
      );
      const profile = workosAuthUser(user, identityClaims(claims));
      return { ...claims, sub: profile.sub, email: profile.email, name: profile.name };
    },

    /**
     * `audience`, `connection` and `prompt` have no AuthKit equivalent and
     * are dropped. `returnTo` rides in `state`. With neither an organization
     * nor an invitation, sign-in goes to Studio's own organization.
     */
    async loginWithRedirect(options) {
      const { invitation, login_hint: loginHint } = options?.authorizationParams ?? {};
      const organization =
        options?.authorizationParams?.organization || (invitation ? undefined : await signInOrganizationId());
      const returnTo = options?.appState?.returnTo;
      const authkit = await client();
      return authkit.signIn({
        ...(organization ? { organizationId: organization } : {}),
        ...(invitation ? { invitationToken: invitation } : {}),
        ...(loginHint ? { loginHint } : {}),
        ...(returnTo ? { state: { returnTo } } : {}),
      });
    },

    async logout(options) {
      const authkit = await client();
      if (options?.openUrl === false) {
        // Ends the WorkOS session in the background without leaving the page.
        // There is none to end when the refresh token was already dead.
        await authkit.signOut({ navigate: false }).catch(() => undefined);
        onSignedOut();
        return;
      }
      try {
        authkit.signOut({ returnTo: options?.logoutParams?.returnTo });
      } catch {
        // No session in memory to end at WorkOS: show the sign-in screen instead.
        onSignedOut();
      }
    },
  };
}

let liveClient: AuthKitClient | null = null;
const waitingForClient: Array<(client: AuthKitClient) => void> = [];

function publishClient(client: AuthKitClient): void {
  liveClient = client;
  for (const resolve of waitingForClient.splice(0)) resolve(client);
}

function readyClient(): Promise<AuthKitClient> {
  if (liveClient) return Promise.resolve(liveClient);
  return new Promise((resolve) => waitingForClient.push(resolve));
}

/**
 * One set of methods for the page, like the OIDC adapter's: the api client
 * registers its token provider once, so nothing it holds may close over a
 * render's context (which starts out as AuthKit's placeholder client).
 */
const METHODS = createWorkOSSessionMethods(readyClient, markSignedOut);

const UNCONFIGURED: AuthSession = {
  isAuthenticated: false,
  isLoading: false,
  getAccessTokenSilently: () => Promise.reject(loginRequiredError('WorkOS provider not mounted')),
  getIdTokenClaims: () => Promise.resolve(undefined),
  loginWithRedirect: () => Promise.reject(new Error('WorkOS provider not mounted')),
  logout: () => Promise.resolve(),
};

function useAuthKitSession(): AuthSession {
  const { isLoading, user, getAccessToken, getUser, signIn, signOut } = useAuth();
  const { identity, signedOut } = useSyncExternalStore(subscribe, readSnapshot, readSnapshot);

  useEffect(() => {
    if (!isLoading) publishClient({ getAccessToken, getUser, signIn, signOut });
  }, [isLoading, getAccessToken, getUser, signIn, signOut]);

  const authUser = useMemo(
    () => (user && !signedOut ? workosAuthUser(user, identity) : undefined),
    [user, identity, signedOut],
  );

  return {
    ...METHODS,
    isAuthenticated: Boolean(authUser),
    isLoading,
    user: authUser,
    error: undefined,
  };
}

function useUnmountedWorkOSSession(): AuthSession {
  return UNCONFIGURED;
}

/**
 * `AuthSession` over `@workos-inc/authkit-react`. AuthKit's `useAuth` throws
 * outside its provider, and the unguarded local shell (no client id) mounts
 * none, so that build gets an anonymous, non-loading session instead.
 *
 * `error` is always unset: AuthKit only logs a failed code exchange and
 * leaves the person signed out, so they land on the sign-in screen.
 */
export const useWorkOSSession: () => AuthSession = AUTH_CONFIGURED
  ? useAuthKitSession
  : useUnmountedWorkOSSession;
